import { Checkbox, Grid2, Tooltip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import React from 'react';
import { Subject, type Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { getNewDimensionSuggestions } from '~/api/dimensions';
import type { DiscreteDimension, DiscreteDimensionAllOptions } from '~/types/api-types';

interface FindDimensionProps {
  timeDimensionId: number | null;
  dimensionTypeId: number | null;
  onSelect: (res: string) => void;
}

interface FindDimensionState {
  open: boolean;
  options: DiscreteDimension[];
  loading: boolean;
  allOptions: DiscreteDimensionAllOptions[];
  seeAll: boolean;
}

export default class FindDimension extends React.Component<FindDimensionProps, FindDimensionState> {
  private readonly subject: Subject<string>;
  private readonly subscription: Subscription;

  constructor(props: FindDimensionProps) {
    super(props);
    this.state = {
      open: false,
      options: [],
      loading: false,
      allOptions: [],
      seeAll: false,
    };

    this.subject = new Subject();
    this.subscription = this.subject.pipe(debounceTime(300)).subscribe((val) => this.search(val));
  }

  public componentWillUnmount(): void {
    this.subscription.unsubscribe();
  }

  private setOpen(value: boolean): void {
    this.setState({ options: value ? this.state.options : [], open: value });
  }

  private async search(value: string): Promise<void> {
    if (!value) {
      this.setState({ options: [] });
      return;
    }

    this.setState({ loading: true });

    const limit = this.state.seeAll ? 0 : 10;
    const dimensions = await getNewDimensionSuggestions({
      name: value,
      prefix: value,
      timeDimensionId: this.props.timeDimensionId,
      dimensionTypeId: this.props.dimensionTypeId,
      limit,
    });

    const tempAllOptions: DiscreteDimensionAllOptions[] = [];
    dimensions.dimensions.forEach((t) => {
      if (t.name) {
        tempAllOptions.push({
          option: t.name,
          discreteDimensionId: t.discreteDimensionId,
          dimensionTypeId: t.dimensionTypeId,
          timeDimensionId: t.timeDimensionId,
        });
      }
      if (t.prefix && t.prefix !== t.name) {
        tempAllOptions.push({
          option: t.prefix,
          discreteDimensionId: t.discreteDimensionId,
          dimensionTypeId: t.dimensionTypeId,
          timeDimensionId: t.timeDimensionId,
        });
      }
    });

    this.sortDimensions(tempAllOptions, value);
    this.setState({
      loading: false,
      options: dimensions.dimensions,
      allOptions: tempAllOptions,
    });
  }

  private sortDimensions(options: DiscreteDimensionAllOptions[], searchValue: string) {
    options.sort((a: DiscreteDimensionAllOptions, b: DiscreteDimensionAllOptions) => {
      const match = a.option.toLowerCase().indexOf(searchValue) - b.option.toLowerCase().indexOf(searchValue);
      if (match === 0) {
        return a.option.toLowerCase().localeCompare(b.option.toLowerCase());
      }
      return match;
    });
  }

  private setSeeAll(value: boolean): void {
    this.setState({ seeAll: value, allOptions: [] });
  }

  private seeAllChange(e: React.ChangeEvent<any>): void {
    this.setSeeAll(e.target.checked);
  }

  public render(): React.ReactNode {
    return (
      <>
        <Grid2 size={'grow'}>
          <Autocomplete
            id="asynchronous-demo"
            blurOnSelect={true}
            fullWidth={true}
            open={this.state.open}
            onOpen={() => this.setOpen(true)}
            onClose={() => this.setOpen(false)}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.option)}
            noOptionsText="Inga resultat"
            options={this.state.allOptions}
            loadingText="Laddar..."
            loading={this.state.loading}
            onChange={(_e: any, v: string | DiscreteDimensionAllOptions | null) => {
              const val = (v as DiscreteDimensionAllOptions)?.option ?? v ?? '';
              if (typeof val === 'string') {
                this.props.onSelect(val);
              }
            }}
            onInputChange={(e, v) => this.subject.next(v)}
            freeSolo={true}
            renderInput={(params) => (
              <TextField
                variant="standard"
                {...params}
                label="Sök dimension"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {this.state.loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={'auto'}>
          <Tooltip title="Visa alla resultat">
            <Checkbox checked={this.state.seeAll} onChange={(e) => this.seeAllChange(e)} />
          </Tooltip>
        </Grid2>
      </>
    );
  }
}
